import { graphql, useFragment } from 'react-relay';

import type { DirectoryFeeBlock_agent$key } from './__generated__/DirectoryFeeBlock_agent.graphql';
import DirectoryAgentClaimRequestDialog from './DirectoryAgentClaimRequestDialog';
import DirectorySourceLinks from './DirectorySourceLinks';

type DirectoryFeeBlockProps = {
  agent: DirectoryFeeBlock_agent$key;
};

const DirectoryFeeBlock = ({ agent: agentKey }: DirectoryFeeBlockProps) => {
  const agent = useFragment(
    graphql`
      fragment DirectoryFeeBlock_agent on LandlordDirectoryAgent {
        id
        name
        slug
        fees {
          management {
            amountPence
            percent
            sourceUrls
          }
          rentCollection {
            amountPence
            percent
            sourceUrls
          }
          tenantFind {
            amountPence
            percent
            sourceUrls
          }
          letOnly {
            amountPence
            percent
            sourceUrls
          }
          renewal {
            amountPence
            percent
            sourceUrls
          }
          inventory {
            amountPence
            percent
            sourceUrls
          }
          managementWithTenantInPlace {
            amountPence
            percent
            sourceUrls
          }
          rentCollectionWithTenantInPlace {
            amountPence
            percent
            sourceUrls
          }
          otherFees {
            label
            amountPence
            percent
            sourceUrls
          }
          feesIncludeVat
          isFromFeeRun
        }
        sources {
          feesIncludeVat
        }
      }
    `,
    agentKey,
  );
  const fees = agent.fees;
  const coreFeeRows = [
    { label: 'Full management', fee: fees?.management },
    { label: 'Rent collection', fee: fees?.rentCollection },
    { label: 'Tenant find', fee: fees?.tenantFind },
  ];
  // A fee run only fills the three core fees and the other fees, so the other
  // fixed rows would always read "Not published" for the branches it has checked.
  const feeRows = fees?.isFromFeeRun
    ? coreFeeRows
    : [
        ...coreFeeRows,
        { label: 'Let only', fee: fees?.letOnly },
        { label: 'Renewal', fee: fees?.renewal },
        { label: 'Inventory', fee: fees?.inventory },
        {
          label: 'Full management, tenant in place',
          fee: fees?.managementWithTenantInPlace,
        },
        {
          label: 'Rent collection, tenant in place',
          fee: fees?.rentCollectionWithTenantInPlace,
        },
      ];
  const otherFees = fees?.otherFees ?? [];
  const hasMissingFee = feeRows.some(
    ({ fee }) => fee?.amountPence == null && fee?.percent == null,
  );

  return (
    <section
      aria-labelledby="agent-fees-heading"
      className="surface-card flex flex-col gap-5 p-5 md:p-7"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2
          className="m-0 text-2xl font-medium tracking-[-0.025em]"
          id="agent-fees-heading"
        >
          Published fees
        </h2>
        <span className="rounded-full bg-accent-tint px-3 py-1 text-xs font-medium text-accent-strong">
          Unverified
        </span>
      </div>
      <p className="m-0 text-sm leading-relaxed text-ink-soft">
        These figures were collected from public sources and have not been
        confirmed by the agent. Ask for a written quote before deciding.
      </p>
      <dl className="m-0 grid gap-3">
        {feeRows.map(({ label, fee }) => (
          <div
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-xl bg-canvas p-4"
            key={label}
          >
            <dt className="flex min-w-0 flex-col items-start gap-1.5 text-sm text-ink-soft">
              {label}
              <DirectorySourceLinks label={label} urls={fee?.sourceUrls} />
            </dt>
            <dd className="m-0 font-medium tabular-nums">
              {formatFee(fee?.amountPence, fee?.percent)}
            </dd>
          </div>
        ))}
      </dl>
      <div className="flex flex-col gap-3">
        <h3 className="m-0 text-base font-medium">Other fees</h3>
        {otherFees.length === 0 ? (
          <p className="m-0 text-sm text-ink-muted">Not published</p>
        ) : (
          <dl className="m-0 grid gap-3">
            {otherFees.map((fee) => (
              <div
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-xl bg-canvas p-4"
                key={`${fee.label ?? ''}-${fee.percent ?? ''}-${fee.amountPence ?? ''}-${fee.sourceUrls?.[0] ?? ''}`}
              >
                <dt className="flex min-w-0 flex-col items-start gap-1.5 text-sm text-ink-soft">
                  {fee.label}
                  <DirectorySourceLinks
                    label={fee.label ?? 'Other fee'}
                    urls={fee.sourceUrls}
                  />
                </dt>
                <dd className="m-0 font-medium tabular-nums">
                  {formatFee(fee.amountPence, fee.percent)}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
      {hasMissingFee && agent.slug ? (
        <div className="flex flex-col items-start gap-4 rounded-2xl bg-accent-tint p-5">
          <div className="flex flex-col gap-1.5">
            <p className="m-0 font-medium text-ink">Work at this agency?</p>
            <p className="m-0 max-w-lg text-sm leading-relaxed text-ink-soft">
              Claim your free profile to add fees and help landlords find and
              compare you in local searches.
            </p>
          </div>
          <DirectoryAgentClaimRequestDialog
            agentId={agent.id}
            agentName={agent.name ?? 'this agent'}
            agentSlug={agent.slug}
            triggerLabel="Get featured in local searches"
          />
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span>
          {fees?.feesIncludeVat == null
            ? 'VAT not recorded'
            : `VAT ${fees.feesIncludeVat ? 'is included' : 'is not included'}`}
        </span>
        <DirectorySourceLinks
          label="VAT"
          urls={agent.sources?.feesIncludeVat}
        />
      </div>
    </section>
  );
};

const formatFee = (
  amountPence: number | null | undefined,
  percent: string | null | undefined,
) => {
  if (percent != null) {
    return `${percent}%`;
  }

  if (amountPence != null) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    }).format(amountPence / 100);
  }

  return 'Not published';
};

export default DirectoryFeeBlock;
